import { useEffect, useRef } from 'react';
import Button from 'react-bootstrap/Button';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { PageId } from '@app/types';
import { requestCollectionWordDelete, requestCollectionWords } from '@app/utils/api';
import { userAtom } from '@app/atoms/userAtom';
import { getWordsDataAtomDefaults, wordsDataAtom, wordsErrorAtom, wordsLoadingAtom, wordsLoadMoreAtom, wordToDeleteAtom } from '@app/atoms/wordsAtoms';
import { selectedEpisodeAtom, selectedMediaAtom, selectedSeasonAtom } from '@app/atoms/selectedMediaAtoms';
import { CollectionWordsResponse, PhrasesResponse, SavedWordData } from '@shared/types/words';
import './CollectionsPanel.scss'
import { UserStatus } from '@shared/types';

const CollectionsPanel = ({ pageId, collectionId }: { pageId?: PageId, collectionId?: string }) => {
  const setSelectedMedia = useSetAtom(selectedMediaAtom);
  const setSelectedSeason = useSetAtom(selectedSeasonAtom);
  const setSelectedEpisode = useSetAtom(selectedEpisodeAtom);
  const [wordsData, setWordsData] = useAtom(wordsDataAtom);
  const [wordsLoading, setWordsLoading] = useAtom(wordsLoadingAtom);
  const setWordsError = useSetAtom(wordsErrorAtom);
  const user = useAtomValue(userAtom);
  const wordsLoadMore = useAtomValue(wordsLoadMoreAtom);
  const [wordToDelete, setWordToDelete] = useAtom(wordToDeleteAtom);

  const loadMoreRef = useRef<number>(wordsLoadMore);
  const wordToDeleteRef = useRef<{ savedWordId: string, collectionId: string } | null>(wordToDelete);
  const wordsDeletedCounterRef = useRef<number>(0);

  const loadCollectionWords = async () => {
    setWordsLoading(true);

    try {
      const { words, part, totalParts, collectionId: _collectionId, collectionName, collectionDescription, error }: CollectionWordsResponse = await requestCollectionWords({
        part: 1,
        collectionId
      });

      if (error) {
        setWordsError(error);
        return;
      }

      setWordsData({ 
        words,
        parts: new Map<number, CollectionWordsResponse>([[1, { words, part, totalParts, collectionId: _collectionId, collectionName, collectionDescription }]]),
        totalParts 
      });

      wordsDeletedCounterRef.current = 0;
    } finally {
      setWordsLoading(false);
    }
  };

  const loadMoreCollectionWords = async ({ collectionId, part }: { collectionId: string, part: number }) => {
    setWordsLoading(true);
    
    try {
      const { words, part: _part, totalParts, collectionId: _collectionId, collectionName, collectionDescription, error }: CollectionWordsResponse = await requestCollectionWords({
        collectionId,
        part,
        shift: wordsDeletedCounterRef.current
      });

      if (error) {
        setWordsError(error);
        return;
      }

      if (words.length && part && !wordsData.parts.has(part)) {
        wordsData.parts.set(part, { 
          words, 
          part: _part, 
          totalParts, 
          collectionId: _collectionId, 
          collectionName, 
          collectionDescription 
        });

        setWordsData({ 
          words: wordsData.words.concat(words),
          parts: wordsData.parts,
          totalParts 
        });

        wordsDeletedCounterRef.current = 0;
      }
    } finally {
      setWordsLoading(false);
    }
  };
  const deleteWordFromCollection = async ({ savedWordId, collectionId }: { savedWordId: string, collectionId: string }) => {
    if (!savedWordId || !collectionId) {
      return;
    }

    const success = await requestCollectionWordDelete({ savedWordId, collectionId });
    if (success) {
      console.log('Word deleted:', savedWordId, collectionId);
      let wordsDataChanged = false;
      const updatedWordsData = {
        ...wordsData,
        words: wordsData.words.filter((word) => {
          if ((word as SavedWordData).savedData?.id !== savedWordId) {
            return true;
          } else {
            wordsDataChanged = true;
            return false;
          }
        })
      };

      if (wordsDataChanged) {

        // also remove the word from parts
        for (const [, part] of updatedWordsData.parts) {
          part.words = (part.words as SavedWordData[]).filter((word) => (word as SavedWordData).savedData?.id !== savedWordId);
        }

        setWordsData(updatedWordsData);
        // "Optimistic" update the wordsData had side efficts - "Load more" worked correctly only after full re-load
        // wordsDeletedCounterRef added to track the number of deleted words and send the "shift" parameter with the next "Load more" request
        wordsDeletedCounterRef.current += 1;
      }
    } else {
      console.error('Failed to delete word:', savedWordId, collectionId);
    }
  }

  useEffect(() => {
    if (user?.status !== UserStatus.Active) {
      return;
    }

    setSelectedMedia(null);
    setSelectedSeason(null);
    setSelectedEpisode(null);

    setWordsData(getWordsDataAtomDefaults());
    setWordsError('');

    if (wordsLoading) {
      return;
    }

    loadCollectionWords();
  }, [pageId, user]);

  useEffect(() => {
    if (user?.status !== UserStatus.Active
      || loadMoreRef.current === wordsLoadMore) {
      return;
    }

    loadMoreRef.current = wordsLoadMore;

    if (wordsLoading) {
      return;
    }

    const lastLoadedPart = Array.from(wordsData.parts.keys()).sort().pop() || 0;

    if (lastLoadedPart < 1) {
      return;
    }

    const lastLoadedPartData: PhrasesResponse | CollectionWordsResponse | undefined = wordsData.parts.get(lastLoadedPart);
    const _collectionId = (lastLoadedPartData as CollectionWordsResponse)?.collectionId;
    
    if (!_collectionId) {
      return;
    }

    loadMoreCollectionWords({ collectionId: _collectionId, part: lastLoadedPart + 1 });
  }, [wordsLoadMore]);

  useEffect(() => {
    if (user?.status !== UserStatus.Active
      || wordToDeleteRef.current === wordToDelete) {
      return;
    }

    wordToDeleteRef.current = wordToDelete;

    if (!wordToDeleteRef.current) {
      return;
    }

    deleteWordFromCollection(wordToDeleteRef.current);
    setWordToDelete(null);
    wordToDeleteRef.current = null;
  }, [wordToDelete]);

  return (<div className="collections-panel">
    <Button variant="outline-secondary" className="d-flex w-100 mb-1" disabled>
      <h3 className="text-start flex-grow-1"><i className="ic ic-bookmark-star" />
        <span>Favorites user collection</span>
        <small>{wordsData.words.length > 0 ? `${wordsData.words.length}` : ''}{wordsData.totalParts && wordsData.totalParts > wordsData.parts.size ? '+' : ''}{wordsData.words.length && ' words and phrases'}</small>
      </h3>
      <div><i className="ic ic-se-selector" /></div>
    </Button>
  </div>);
};

export default CollectionsPanel;
